/**
 * useTickets Hook
 */

import { useCallback, useEffect, useState } from 'react';
import { listUserTickets } from '../api';
import type { SupportTicket, TicketStatus } from '../types';

interface UseTicketsOptions {
  status?: TicketStatus;
  limit?: number;
}

interface UseTicketsReturn {
  tickets: SupportTicket[];
  total: number;
  isLoading: boolean;
  error: string | null;
  hasMore: boolean;
  refresh: () => Promise<void>;
  loadMore: () => Promise<void>;
}

export function useTickets(options: UseTicketsOptions = {}): UseTicketsReturn {
  const { status, limit = 20 } = options;

  const [tickets, setTickets] = useState<SupportTicket[]>([]);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [offset, setOffset] = useState(0);

  const fetchTickets = useCallback(async (reset = false) => {
    setIsLoading(true);
    setError(null);

    const currentOffset = reset ? 0 : offset;

    try {
      const response = await listUserTickets(status, limit, currentOffset);

      if (!response.success) {
        setError(response.error || 'Failed to load tickets');
        return;
      }

      setTotal(response.total);
      setTickets(prev =>
        reset ? response.tickets : [...prev, ...response.tickets]
      );
      setOffset(currentOffset + response.tickets.length);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load tickets');
    } finally {
      setIsLoading(false);
    }
  }, [status, limit, offset]);

  const refresh = useCallback(async () => {
    setOffset(0);
    await fetchTickets(true);
  }, [fetchTickets]);

  const loadMore = useCallback(async () => {
    if (!isLoading && tickets.length < total) {
      await fetchTickets(false);
    }
  }, [fetchTickets, isLoading, tickets.length, total]);

  // Initial fetch
  useEffect(() => {
    fetchTickets(true);
  }, [status]); // Reset when status changes

  return {
    tickets,
    total,
    isLoading,
    error,
    hasMore: tickets.length < total,
    refresh,
    loadMore,
  };
}
